// Vite entrypoint - rebuild trigger
console.log("Vite ⚡️ Ruby")

import Rails from "@rails/ujs"
Rails.start()

import * as Turbo from "@hotwired/turbo"
Turbo.session.drive = false

// --------------------
// CSS / Assets
// --------------------
import "../sass/smartapp.scss"
import "../webfonts/smartadmin/sa-icons.scss"
import "../webfonts/fontawesome/fontawesome.scss"
import {saveSettings} from "../scripts/core/saveloadscript"
import bootstrap from "bootstrap/dist/js/bootstrap"

window.bootstrap ||= bootstrap

const layoutObserver = new MutationObserver(() => {
    saveSettings();
});

layoutObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-bs-theme', 'data-menu-hub-layout']
});

document.addEventListener("turbo:load", () => {
    const saved = JSON.parse(localStorage.getItem('layoutSettings') || '{}');
    if (saved.theme) {
        document.documentElement.setAttribute('data-bs-theme', saved.theme);
    }
});


// --------------------
// jQuery (global)
// --------------------
import $ from "jquery"
window.$ = $
window.jQuery = $

// --------------------
// ApexCharts (SmartAdmin wrapper expects globals)
// --------------------
import ApexCharts from "apexcharts"

// Alguns wrappers do SmartAdmin esperam "OriginalApexCharts" e/ou "ApexCharts" no window
window.OriginalApexCharts = ApexCharts
window.ApexCharts = ApexCharts

// --------------------
// DataTables
// --------------------
import "datatables.net-bs5"
import "datatables.net-bs5/css/dataTables.bootstrap5.css"
import DataTable from "datatables.net-bs5"
import datatablesPtBr from "../../config/datatables/i18n/pt-BR.json"

$.extend(true, DataTable.defaults, { language: datatablesPtBr })

// --------------------
// Flatpickr
// --------------------
import "flatpickr/dist/flatpickr.min.css"

// --------------------
// Stimulus
// --------------------
import {Application} from "@hotwired/stimulus"
import {registerUiComponents} from "ui_components"

const application = Application.start()

// --------------------
// 🔹 Controllers da GEM (registrar primeiro — app override depois)
// --------------------
registerUiComponents(application)

// --------------------
// 🔹 Controllers da APP (sobrescreve gem quando mesmo nome)
// --------------------
const controllers = import.meta.glob(
    "../../app/javascript/controllers/**/*_controller.js",
    {eager: true}
)

for (const path in controllers) {
    const controller = controllers[path]
    const name = path
        .split("/")
        .pop()
        .replace("_controller.js", "")
        .replace(/_/g, "-")

    application.register(name, controller.default)
}

// -------------------------
// Ativar Tooltips Bootstrap
// -------------------------
function initTooltip(el) {
    if (!bootstrap.Tooltip.getInstance(el)) {
        new bootstrap.Tooltip(el)
    }
}

function initTooltipsIn(root) {
    if (root.matches && root.matches('[data-bs-toggle="tooltip"]')) {
        initTooltip(root)
    }
    if (root.querySelectorAll) {
        root.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(initTooltip)
    }
}

document.addEventListener("turbo:load", () => {
    initTooltipsIn(document)

    // Observa novos nós no DOM (DataTables AJAX, Turbo Frames, etc.)
    new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const node of m.addedNodes) {
                if (node.nodeType === 1) initTooltipsIn(node)
            }
        }
    }).observe(document.body, { childList: true, subtree: true })
})

// ----------------------------------------
// Notificações globais compatíveis com window.toastr
// ----------------------------------------

const toastClasses = {
    success: "text-bg-success",
    error: "text-bg-danger",
    warning: "text-bg-warning",
    info: "text-bg-info",
}

const toastDefaults = {
    closeButton: true,
    newestOnTop: true,
    preventDuplicates: true,
    positionClass: "toast-top-right",
    timeOut: 4000,
}

let previousToastMessage = null

function toastContainer() {
    let container = document.getElementById("toast-container")

    if (!container) {
        container = document.createElement("div")
        container.id = "toast-container"
        container.className = "toast-container position-fixed top-0 end-0 p-3"
        container.style.zIndex = "1100"
        document.body.appendChild(container)
    }

    return container
}

function showToast(type, message, title, options = {}) {
    const settings = {...toastDefaults, ...window.toastr.options, ...options}
    const normalizedMessage = message == null ? "" : String(message)

    if (settings.preventDuplicates && previousToastMessage === normalizedMessage) return
    previousToastMessage = normalizedMessage

    const toast = document.createElement("div")
    toast.className = `toast align-items-center border-0 ${toastClasses[type] || toastClasses.info}`
    toast.setAttribute("role", type === "error" ? "alert" : "status")
    toast.setAttribute("aria-live", type === "error" ? "assertive" : "polite")
    toast.setAttribute("aria-atomic", "true")

    const content = document.createElement("div")
    content.className = "d-flex"

    const body = document.createElement("div")
    body.className = "toast-body"

    if (title) {
        const titleElement = document.createElement("strong")
        titleElement.className = "d-block"
        titleElement.textContent = String(title)
        body.appendChild(titleElement)
    }

    const messageElement = document.createElement("span")
    messageElement.textContent = normalizedMessage
    body.appendChild(messageElement)
    content.appendChild(body)

    if (settings.closeButton) {
        const closeButton = document.createElement("button")
        closeButton.type = "button"
        closeButton.className = "btn-close btn-close-white me-2 m-auto"
        closeButton.setAttribute("data-bs-dismiss", "toast")
        closeButton.setAttribute("aria-label", "Fechar")
        content.appendChild(closeButton)
    }

    toast.appendChild(content)

    const container = toastContainer()
    if (settings.newestOnTop) {
        container.prepend(toast)
    } else {
        container.appendChild(toast)
    }

    const toastInstance = new window.bootstrap.Toast(toast, {
        delay: settings.timeOut,
        autohide: settings.timeOut > 0,
    })

    toast.addEventListener("hidden.bs.toast", () => toast.remove())
    toastInstance.show()
}

window.toastr = {
    options: toastDefaults,
    success: (message, title, options) => showToast("success", message, title, options),
    error: (message, title, options) => showToast("error", message, title, options),
    warning: (message, title, options) => showToast("warning", message, title, options),
    info: (message, title, options) => showToast("info", message, title, options),
}

// ----------------------------------------
// Busca em selects com TomSelect
// ----------------------------------------
import TomSelect from "tom-select"
import "tom-select/dist/css/tom-select.css"

window.TomSelect = TomSelect

// ----------------------------------------
// Remote Modal (links com data-remote-modal)
// ----------------------------------------
document.addEventListener("click", async (event) => {
    const link = event.target.closest("[data-remote-modal]");
    if (!link) return;
    event.preventDefault();

    const url = link.getAttribute("href") || link.dataset.remoteModalUrlValue;
    if (!url) return;

    try {
        const response = await fetch(url, {
            headers: {
                "Accept": "text/html",
                "X-Requested-With": "XMLHttpRequest"
            }
        });
        if (!response.ok) return;

        const html = await response.text();

        let container = document.getElementById("remote-modal-container");
        if (!container) {
            container = document.createElement("div");
            container.id = "remote-modal-container";
            document.body.appendChild(container);
        }
        container.innerHTML = html;

        const modalEl = container.querySelector(".modal");
        if (modalEl) {
            const ModalClass = window.bootstrap?.Modal || bootstrap?.Modal;
            if (ModalClass) {
                const modal = new ModalClass(modalEl);
                modal.show();
                modalEl.addEventListener("hidden.bs.modal", () => {
                    container.innerHTML = "";
                }, { once: true });
            }
        }
    } catch (e) {
        console.error("[remote-modal] Erro:", e);
    }
});
